import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  Chip,
  CircularProgress,
  LinearProgress,
  Alert,
  TableSortLabel,
  IconButton,
  Collapse,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ButtonGroup,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
} from '@mui/material';
import {
  Description,
  Assessment,
  TrendingUp,
  People,
  AttachMoney,
  Business,
  FileDownload,
  Search,
  ExpandMore,
  ExpandLess,
  FilterList,
  Visibility,
  PictureAsPdf,
  ArrowBack,
  ArrowForward,
  Receipt,
  Work,
  Payment,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { requestService } from '../services/requestService';
import { NumberTextField } from '../components/common';
import { serviceProviderService } from '../services/serviceProviderService';
import { insuranceCompanyService } from '../services/insuranceCompanyService';
import employeeService from '../services/employee.service';
import InvoiceService from '../services/invoice.service';
import departmentServiceInstance from '../services/department.service';
import PayrollService from '../services/payroll.service';
import api from '../api/axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { addLogoToPDF } from '../utils/pdfLogoHelper';

type ReportType = 'requests' | 'employees' | 'invoices' | 'payrolls' | '';
type InvoiceSubType = 'all' | 'user' | 'insurance';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

interface FilterState {
  // Requests filters
  requestStatus?: string;
  requestPriority?: string;
  requestServiceProvider?: string;
  requestServiceType?: string;
  requestInsuranceCompany?: string;
  
  // Employees filters
  employeeStatus?: string;
  employeeDepartment?: string;
  employeePosition?: string;
  employeeEmploymentType?: string;
  
  // Invoices filters
  invoiceStatus?: string;
  invoiceType?: string;
  invoiceMinAmount?: string;
  invoiceMaxAmount?: string;
  invoiceHasExtraKm?: string;
  invoiceHasInsuranceServiceCharge?: string;
  
  // Payrolls filters
  payrollStatus?: string;
  payrollPeriod?: string;
}

const Reports: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [reportType, setReportType] = useState<ReportType>('');
  const [invoiceSubType, setInvoiceSubType] = useState<InvoiceSubType>('all');
  const [startDate, setStartDate] = useState<string>(() => {
    const date = new Date();
    date.setDate(1);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState<string>(() => {
    return new Date().toISOString().split('T')[0];
  });
  // Month/Year selection for requests report
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return { month: now.getMonth() + 1, year: now.getFullYear() };
  });
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [error, setError] = useState<string>('');
  const [data, setData] = useState<any[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [filters, setFilters] = useState<FilterState>({});
  const [departments, setDepartments] = useState<any[]>([]);
  const [serviceProviders, setServiceProviders] = useState<any[]>([]);
  const [insuranceCompanies, setInsuranceCompanies] = useState<any[]>([]);
  const [invoiceItems, setInvoiceItems] = useState<Record<number, any[]>>({});
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [viewingRequest, setViewingRequest] = useState<any>(null);
  const [imageErrors, setImageErrors] = useState({ before: false, after: false });

  // Helper function to get image URL from photo data
  const getImageUrl = (photoData: any): string | null => {
    if (!photoData) return null;
    
    let url = photoData?.data?.attributes?.url || 
              photoData?.url || 
              photoData?.data?.url || 
              photoData?.attributes?.url || 
              '';
    
    // Check if URL is valid (not empty, not null, not undefined)
    if (!url || typeof url !== 'string' || url.trim() === '' || url === 'null' || url === 'undefined') {
      return null;
    }
    
    // If URL is relative (starts with /), prepend the API base URL
    // Strapi uploads are served from the root, not from /api
    if (url.startsWith('/')) {
      const API_URL = process.env.REACT_APP_API_URL || 'https://darb.aiastra.io';
      url = `${API_URL}${url}`;
    }
    
    return url;
  };

  // Helper function to check if image data exists and has valid URL
  const hasValidImage = (photoData: any): boolean => {
    if (!photoData) return false;
    const url = getImageUrl(photoData);
    return url !== null && url !== '';
  };

  // Fetch departments and service providers for filters
  useEffect(() => {
    if (reportType === 'employees') {
      departmentServiceInstance.getDepartments({ 'pagination[pageSize]': 100 }).then((res) => {
        setDepartments(res.data?.data || []);
      });
    }
    if (reportType === 'requests') {
      serviceProviderService.getAll({ 'pagination[pageSize]': 100 }).then((res) => {
        setServiceProviders(res.data?.data || []);
      });
      insuranceCompanyService.getAll().then((data) => {
        setInsuranceCompanies(Array.isArray(data) ? data : []);
      });
    }
  }, [reportType]);

  // Fetch data based on report type
  useEffect(() => {
    // Clear all data and UI state immediately when report type changes
    setData([]);
    setFilteredData([]);
    setLoadingProgress(0);
    setExpandedRows(new Set()); // Clear expanded rows
    setError(''); // Clear any previous errors
    
    if (reportType) {
      // For requests, only load if month is selected
      if (reportType === 'requests') {
        // Don't auto-load, wait for user to select month and company
        // Data already cleared above
      } else {
        // Fetch data for other report types
        fetchReportData();
      }
    }
  }, [reportType, startDate, endDate, invoiceSubType]);

  // Load data for requests when reportType changes to 'requests', month or company filter changes
  useEffect(() => {
    if (reportType === 'requests' && selectedMonth) {
      fetchReportData();
    }
  }, [reportType, selectedMonth, filters.requestInsuranceCompany]);

  // Filter and sort data
  useEffect(() => {
    let result = [...data];

    // Apply search filter
    if (searchTerm) {
      result = result.filter((item) => {
        const attrs = item.attributes || item;
        const searchLower = searchTerm.toLowerCase();
        
        switch (reportType) {
          case 'requests':
            return (
              attrs.requestNumber?.toLowerCase().includes(searchLower) ||
              attrs.description?.toLowerCase().includes(searchLower) ||
              attrs.status?.toLowerCase().includes(searchLower)
            );
          case 'employees':
            return (
              attrs.employeeNumber?.toLowerCase().includes(searchLower) ||
              attrs.firstName?.toLowerCase().includes(searchLower) ||
              attrs.lastName?.toLowerCase().includes(searchLower) ||
              attrs.email?.toLowerCase().includes(searchLower)
            );
          case 'invoices':
            return (
              attrs.invoiceNumber?.toLowerCase().includes(searchLower)
            );
          case 'payrolls':
            return (
              attrs.period?.toLowerCase().includes(searchLower) ||
              attrs.status?.toLowerCase().includes(searchLower) ||
              attrs.employee?.data?.attributes?.firstName?.toLowerCase().includes(searchLower) ||
              attrs.employee?.data?.attributes?.lastName?.toLowerCase().includes(searchLower)
            );
          default:
            return true;
        }
      });
    }

    // Apply property filters
    result = result.filter((item) => {
      const attrs = item.attributes || item;
      
      switch (reportType) {
        case 'requests':
          if (filters.requestStatus && attrs.status !== filters.requestStatus) return false;
          if (filters.requestPriority && attrs.priority !== filters.requestPriority) return false;
          if (filters.requestServiceProvider) {
            const providerId = attrs.serviceProvider?.data?.id || attrs.serviceProvider?.id;
            if (String(providerId) !== filters.requestServiceProvider) return false;
          }
          if (filters.requestServiceType && attrs.serviceType !== filters.requestServiceType) return false;
          if (filters.requestInsuranceCompany) {
            const insuranceClient = attrs.insuranceClient?.data?.attributes || attrs.insuranceClient;
            const companyId = insuranceClient?.insuranceCompany?.data?.id || insuranceClient?.insuranceCompany?.id;
            if (!companyId || String(companyId) !== filters.requestInsuranceCompany) return false;
          }
          break;
        case 'employees':
          if (filters.employeeStatus && attrs.status !== filters.employeeStatus) return false;
          if (filters.employeeDepartment) {
            const deptId = attrs.department?.data?.id || attrs.department?.id;
            if (String(deptId) !== filters.employeeDepartment) return false;
          }
          if (filters.employeePosition && attrs.position !== filters.employeePosition) return false;
          if (filters.employeeEmploymentType && attrs.employmentType !== filters.employeeEmploymentType) return false;
          break;
        case 'invoices':
          // Apply invoice sub-type filter based on invoiceType field
          if (invoiceSubType === 'user') {
            // User invoices: invoices with invoiceType === 'service_provider_to_darb'
            if (attrs.invoiceType !== 'service_provider_to_darb') return false;
          } else if (invoiceSubType === 'insurance') {
            // Insurance invoices: invoices with invoiceType === 'insurance_company_monthly'
            if (attrs.invoiceType !== 'insurance_company_monthly') return false;
          }
          
          if (filters.invoiceStatus && attrs.status !== filters.invoiceStatus) return false;
          if (filters.invoiceType && attrs.invoiceType !== filters.invoiceType) return false;
          if (filters.invoiceMinAmount) {
            const total = parseFloat(attrs.total || 0);
            if (total < parseFloat(filters.invoiceMinAmount)) return false;
          }
          if (filters.invoiceMaxAmount) {
            const total = parseFloat(attrs.total || 0);
            if (total > parseFloat(filters.invoiceMaxAmount)) return false;
          }
          if (filters.invoiceHasExtraKm === 'true') {
            const invoiceId = item.id;
            const items = invoiceItems[invoiceId] || [];
            const hasExtraKm = items.some((item: any) => {
              const itemAttrs = item.attributes || item;
              const product = itemAttrs.product?.data?.attributes || itemAttrs.product;
              const productName = (product?.name || '').toLowerCase();
              const description = (itemAttrs.description || '').toLowerCase();
              return productName.includes('km') || productName.includes('kilometer') ||
                     description.includes('km') || description.includes('kilometer');
            });
            if (!hasExtraKm) return false;
          }
          break;
        case 'payrolls':
          if (filters.payrollStatus && attrs.status !== filters.payrollStatus) return false;
          if (filters.payrollPeriod && attrs.period !== filters.payrollPeriod) return false;
          break;
      }
      return true;
    });

    // Apply sorting
    if (sortField) {
      result.sort((a, b) => {
        const aAttrs = a.attributes || a;
        const bAttrs = b.attributes || b;
        let aValue = aAttrs[sortField];
        let bValue = bAttrs[sortField];
        
        // Handle nested values
        if (sortField === 'serviceProvider') {
          aValue = aAttrs.serviceProvider?.data?.attributes?.name || aAttrs.serviceProvider?.name || '';
          bValue = bAttrs.serviceProvider?.data?.attributes?.name || bAttrs.serviceProvider?.name || '';
        } else if (sortField === 'customer') {
          aValue = aAttrs.customer?.data?.attributes?.name || aAttrs.customer?.name || '';
          bValue = bAttrs.customer?.data?.attributes?.name || bAttrs.customer?.name || '';
        } else if (sortField === 'department') {
          aValue = aAttrs.department?.data?.attributes?.name || aAttrs.department?.name || '';
          bValue = bAttrs.department?.data?.attributes?.name || bAttrs.department?.name || '';
        } else if (sortField === 'invoiceType') {
          // Sort by invoice type: determine display type for comparison based on invoiceType field
          const aDbType = aAttrs.invoiceType || '';
          aValue = aDbType === 'insurance_company_monthly' ? 'Insurance' : 
                   aDbType === 'service_provider_monthly' ? 'Service Provider' : 'Client';
          
          const bDbType = bAttrs.invoiceType || '';
          bValue = bDbType === 'insurance_company_monthly' ? 'Insurance' : 
                   bDbType === 'service_provider_monthly' ? 'Service Provider' : 'Client';
        }
        
        if (aValue === undefined || aValue === null) return 1;
        if (bValue === undefined || bValue === null) return -1;
        
        const comparison = aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
        return sortDirection === 'asc' ? comparison : -comparison;
      });
    }

    setFilteredData(result);
  }, [data, searchTerm, sortField, sortDirection, reportType, filters, invoiceSubType]);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      setLoadingProgress(0); // Reset progress when starting new fetch
      setError('');
      
      // For requests report, use month/year; for others, use date range
      let start: string;
      let end: string;
      
      if (reportType === 'requests') {
        // Calculate date range for selected month
        const startDate = new Date(selectedMonth.year, selectedMonth.month - 1, 1);
        const endDate = new Date(selectedMonth.year, selectedMonth.month, 0, 23, 59, 59, 999);
        start = startDate.toISOString();
        end = endDate.toISOString();
      } else {
        start = `${startDate}T00:00:00.000Z`;
        end = `${endDate}T23:59:59.999Z`;
      }

      let response: any;

      let items: any[] = [];

      try {
        switch (reportType) {
          case 'requests':
            // Fetch all requests with optimized populate - removed unused invoice and customer
            // Increased page size to 1000 to reduce API calls significantly
            let allRequests: any[] = [];
            let page = 1;
            let hasMore = true;
            let totalPages = 1;
            
            // Reset progress at start
            setLoadingProgress(0);
            
            while (hasMore) {
              response = await requestService.getAll({
                // Only populate what's needed for display and filtering
                'populate[serviceProvider]': '*', // Needed for filtering
                // Removed invoice and customer - not used in report display
                'populate[insuranceClient]': {
                  populate: ['insuranceCompany']
                },
                'filters[createdAt][$gte]': start,
                'filters[createdAt][$lte]': end,
                'filters[status][$eq]': 'completed', // Only show completed requests
                'pagination[page]': page,
                'pagination[pageSize]': 1000, // Increased from 100 to 10x fewer API calls
              });
              
              const pageData = response?.data || [];
              const pagination = response?.meta?.pagination || {};
              
              // Get total pages from first response
              if (page === 1) {
                totalPages = pagination.pageCount || 1;
              }
              
              allRequests = [...allRequests, ...pageData];
              
              // Calculate and update progress: (currentPage / totalPages) * 100
              const progress = Math.min(Math.round((page / totalPages) * 100), 100);
              setLoadingProgress(progress);
              
              hasMore = page < totalPages;
              page++;
            }
            
            // Ensure progress reaches 100% when complete
            setLoadingProgress(100);
            items = allRequests;
            break;
          case 'employees':
            // Fetch all employees by getting all pages
            let allEmployees: any[] = [];
            let empPage = 1;
            let empHasMore = true;
            let empTotalPages = 1;
            
            // Reset progress at start
            setLoadingProgress(0);
            
            while (empHasMore) {
              response = await employeeService.getAll({
                'populate[department]': '*',
                'pagination[page]': empPage,
                'pagination[pageSize]': 1000, // Increased page size for fewer API calls
              });
              
              const pageData = response?.data?.data || [];
              const pagination = response?.data?.meta?.pagination || {};
              
              // Get total pages from first response
              if (empPage === 1) {
                empTotalPages = pagination.pageCount || 1;
              }
              
              allEmployees = [...allEmployees, ...pageData];
              
              // Calculate and update progress: (currentPage / totalPages) * 100
              const progress = Math.min(Math.round((empPage / empTotalPages) * 100), 100);
              setLoadingProgress(progress);
              
              empHasMore = empPage < empTotalPages;
              empPage++;
            }
            
            // Ensure progress reaches 100% when complete
            setLoadingProgress(100);
            items = allEmployees;
            break;
          case 'invoices':
            // Optimize: Only populate minimal relations needed for filtering and display
            response = await InvoiceService.getAll({
              'populate[insuranceClient][populate][insuranceCompany]': '*',
              'populate[request]': '*',
              'pagination[pageSize]': 1000, // Fetch more to filter client-side
            });
            // InvoiceService.getAll returns full axios response
            // So response.data is { data: [...], meta: {...} }, and response.data.data is the array
            items = response?.data?.data || [];
            
            // Filter by date: use request completedDate for imported requests, invoice createdAt for others
            items = items.filter((invoice: any) => {
              const attrs = invoice.attributes || invoice;
              const request = attrs.request?.data?.attributes || attrs.request?.data || attrs.request;
              const isImported = request?.imported || request?.attributes?.imported;
              
              let dateToCheck: string | null = null;
              if (isImported && request?.completedDate) {
                // For imported requests, use completedDate (the actual completion date from Excel)
                dateToCheck = request.completedDate || request.attributes?.completedDate;
              } else {
                // For non-imported requests, use invoice createdAt
                dateToCheck = attrs.createdAt || invoice.createdAt;
              }
              
              if (!dateToCheck) return false;
              
              const invoiceDate = new Date(dateToCheck);
              const startDate = new Date(start);
              const endDate = new Date(end);
              endDate.setHours(23, 59, 59, 999); // Include full end date
              
              return invoiceDate >= startDate && invoiceDate <= endDate;
            });
            
            // Don't fetch invoice items upfront - fetch them lazily when row is expanded
            setInvoiceItems({});
            break;
          case 'payrolls':
            response = await PayrollService.getAll({
              'populate[employee]': '*',
              'populate[processedBy]': '*',
              'filters[createdAt][$gte]': start,
              'filters[createdAt][$lte]': end,
              'pagination[pageSize]': 100,
            });
            // PayrollService.getAll returns full axios response
            // So response.data is { data: [...], meta: {...} }, and response.data.data is the array
            items = response?.data?.data || [];
            break;
        }

        // Ensure items is always an array
        if (!Array.isArray(items)) {
          console.error('Items is not an array. Response structure:', response);
          // Try to extract array from different possible structures
          if (response?.data && Array.isArray(response.data)) {
            items = response.data;
          } else if (response?.data?.data && Array.isArray(response.data.data)) {
            items = response.data.data;
          } else {
            items = [];
          }
        }
      } catch (fetchError: any) {
        console.error('Error in fetchReportData switch:', fetchError);
        items = [];
      }

      setData(items);
    } catch (err: any) {
      console.error('Error fetching report data:', err);
      setError('Failed to load report data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: keyof FilterState, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value || undefined,
    }));
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const toggleRowExpand = async (id: number) => {
    const newExpanded = new Set(expandedRows);
    const isCurrentlyExpanded = newExpanded.has(id);
    
    if (isCurrentlyExpanded) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
      // Lazy load invoice items when expanding invoice row
      if (reportType === 'invoices' && !invoiceItems[id]) {
        try {
          const itemsResponse = await InvoiceService.getItems(id);
          setInvoiceItems((prev) => ({
            ...prev,
            [id]: itemsResponse.data?.data || [],
          }));
        } catch (err) {
          console.error(`Error fetching items for invoice ${id}:`, err);
          setInvoiceItems((prev) => ({
            ...prev,
            [id]: [],
          }));
        }
      }
    }
    setExpandedRows(newExpanded);
  };

  // Get unique values for filter dropdowns
  const getUniqueValues = (field: string) => {
    const values = new Set<string>();
    data.forEach((item) => {
      const attrs = item.attributes || item;
      let value: any;
      
      if (field === 'area') {
        value = attrs.area;
      } else if (field === 'serviceType') {
        value = attrs.serviceType;
      } else if (field === 'position') {
        value = attrs.position;
      }
      
      if (value) values.add(value);
    });
    return Array.from(values).sort();
  };

  // Generate chart data
  const chartData = useMemo(() => {
    if (!reportType || filteredData.length === 0) return null;

    switch (reportType) {
      case 'requests':
        const statusCounts: Record<string, number> = {};
        filteredData.forEach((item) => {
          const status = (item.attributes || item).status || 'unknown';
          statusCounts[status] = (statusCounts[status] || 0) + 1;
        });
        
        const dailyCounts: Record<string, number> = {};
        filteredData.forEach((item) => {
          const date = new Date((item.attributes || item).createdAt || item.createdAt);
          const dateKey = date.toISOString().split('T')[0];
          dailyCounts[dateKey] = (dailyCounts[dateKey] || 0) + 1;
        });

        return {
          statusPie: Object.entries(statusCounts).map(([name, value]) => ({ name, value })),
          dailyLine: Object.entries(dailyCounts)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([date, count]) => ({ date: date.split('-')[2], count })),
        };

      case 'employees':
        const deptCounts: Record<string, number> = {};
        filteredData.forEach((item) => {
          const attrs = item.attributes || item;
          const dept = attrs.department?.data?.attributes?.name || attrs.department?.name || 'No Department';
          deptCounts[dept] = (deptCounts[dept] || 0) + 1;
        });

        const empStatusCounts: Record<string, number> = {};
        filteredData.forEach((item) => {
          const status = (item.attributes || item).status || 'unknown';
          empStatusCounts[status] = (empStatusCounts[status] || 0) + 1;
        });

        return {
          deptPie: Object.entries(deptCounts).map(([name, value]) => ({ name, value })),
          statusBar: Object.entries(empStatusCounts).map(([name, value]) => ({ name, value })),
        };

      case 'invoices':
        const invStatusCounts: Record<string, number> = {};
        const monthlyRevenue: Record<string, number> = {};
        
        filteredData.forEach((item) => {
          const attrs = item.attributes || item;
          const status = attrs.status || 'unknown';
          // Skip draft status in charts since all invoices are draft by default
          if (status !== 'draft' && status !== 'unknown') {
          invStatusCounts[status] = (invStatusCounts[status] || 0) + 1;
          }
          
          const date = new Date(attrs.invoiceDate || attrs.createdAt);
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          monthlyRevenue[monthKey] = (monthlyRevenue[monthKey] || 0) + (attrs.total || 0);
        });

        return {
          statusPie: Object.entries(invStatusCounts).map(([name, value]) => ({ name, value })),
          revenueLine: Object.entries(monthlyRevenue)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([month, revenue]) => ({ month: month.split('-')[1], revenue: Number(revenue) })),
        };

      case 'payrolls':
        const payrollStatusCounts: Record<string, number> = {};
        const monthlyPayroll: Record<string, number> = {};
        
        filteredData.forEach((item) => {
          const attrs = item.attributes || item;
          const status = attrs.status || 'unknown';
          payrollStatusCounts[status] = (payrollStatusCounts[status] || 0) + 1;
          
          if (attrs.period) {
            monthlyPayroll[attrs.period] = (monthlyPayroll[attrs.period] || 0) + (attrs.netSalary || 0);
          }
        });

        return {
          statusPie: Object.entries(payrollStatusCounts).map(([name, value]) => ({ name, value })),
          revenueLine: Object.entries(monthlyPayroll)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([period, amount]) => ({ month: period.split('-')[1], revenue: Number(amount) })),
        };

      default:
        return null;
    }
  }, [reportType, filteredData]);

  // CSV Export
  const handleDownloadPDF = async () => {
    if (filteredData.length === 0 || reportType !== 'requests') return;

    const doc = new jsPDF('landscape', 'mm', 'a4');
    
    // Add logo
    await addLogoToPDF(doc);

    // Report title - add more space after logo (logo is at y=10, height ~17, so start at y=35)
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Service Request Report', 20, 40);

    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    const monthName = new Date(selectedMonth.year, selectedMonth.month - 1).toLocaleString('default', { month: 'long', year: 'numeric' });
    doc.text(`Period: ${monthName}`, 20, 47);
    doc.text(`Total Requests: ${filteredData.length}`, 20, 53);

    // Prepare table data
    const tableData = filteredData.map((item) => {
      const attrs = item.attributes || item;
      const insuranceClient = attrs.insuranceClient?.data?.attributes || attrs.insuranceClient;
      
      // Get vehicle info
      const vehicle = insuranceClient 
        ? `${insuranceClient.carMake || ''} ${insuranceClient.carModel || ''}`.trim() || 'N/A'
        : 'N/A';
      
      const year = insuranceClient?.carYear || 'N/A';
      const plate = insuranceClient?.plateNumber || 'N/A';
      const policyNumber = insuranceClient?.policyNumber || 'N/A';
      
      // Get insurance company name
      const insuranceCompany = insuranceClient?.insuranceCompany?.data?.attributes || insuranceClient?.insuranceCompany?.data || insuranceClient?.insuranceCompany;
      const companyName = insuranceCompany?.name || 'N/A';
      
      // Format reasons
      let reasons = 'N/A';
      if (Array.isArray(attrs.reasons) && attrs.reasons.length > 0) {
        reasons = attrs.reasons.join(', ');
      } else if (attrs.description) {
        reasons = attrs.description;
      }
      
      return [
        attrs.createdAt ? new Date(attrs.createdAt).toLocaleDateString() : 'N/A',
        attrs.completedDate ? new Date(attrs.completedDate).toLocaleDateString() : 'N/A',
        attrs.requestNumber || `REQ-${item.id}`,
        vehicle,
        year?.toString() || 'N/A',
        plate,
        attrs.phoneNumber || 'N/A',
        attrs.locationText || 'N/A',
        attrs.toLocationText || attrs.toDropDown || 'N/A',
        attrs.serviceType || 'N/A',
        reasons,
        policyNumber,
        companyName,
      ];
    });

    // Add table
    autoTable(doc, {
      startY: 60,
      head: [['Created At', 'Complete Date', 'Request ID', 'Vehicle', 'Year', 'Plate', 'Phone', 'From', 'To', 'Service Type', 'Reason', 'Policy Number', 'Company']],
      body: tableData,
      theme: 'grid',
      headStyles: {
        fillColor: [33, 150, 243],
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 8,
      },
      styles: {
        fontSize: 7,
        cellPadding: 2,
        overflow: 'linebreak',
      },
      columnStyles: {
        0: { cellWidth: 18 }, // Created At
        1: { cellWidth: 18 }, // Complete Date
        2: { cellWidth: 18 }, // Request ID
        3: { cellWidth: 22 }, // Vehicle
        4: { cellWidth: 11 }, // Year
        5: { cellWidth: 14 }, // Plate
        6: { cellWidth: 18 }, // Phone
        7: { cellWidth: 25 }, // From
        8: { cellWidth: 25 }, // To
        9: { cellWidth: 20 }, // Service Type
        10: { cellWidth: 30 }, // Reason
        11: { cellWidth: 20 }, // Policy Number
        12: { cellWidth: 20 }, // Company
      },
      margin: { left: 15, right: 15 },
    });

    // Generate filename
    const filename = `Service-Request-Report-${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(filename);
  };

  const exportToCSV = () => {
    if (filteredData.length === 0) return;

    let headers: string[] = [];
    let rows: string[][] = [];

    switch (reportType) {
      case 'requests':
        headers = ['Created At', 'Complete Date', 'Request ID', 'Vehicle', 'Year', 'Plate', 'Phone', 'From', 'To', 'Service Type', 'Reason', 'Policy Number', 'Company'];
        rows = filteredData.map((item) => {
          const attrs = item.attributes || item;
          const insuranceClient = attrs.insuranceClient?.data?.attributes || attrs.insuranceClient;
          
          // Get vehicle info
          const vehicle = insuranceClient 
            ? `${insuranceClient.carMake || ''} ${insuranceClient.carModel || ''}`.trim() || 'N/A'
            : 'N/A';
          
          const year = insuranceClient?.carYear || 'N/A';
          const plate = insuranceClient?.plateNumber || 'N/A';
          const policyNumber = insuranceClient?.policyNumber || 'N/A';
          
          // Get insurance company name
          const insuranceCompany = insuranceClient?.insuranceCompany?.data?.attributes || insuranceClient?.insuranceCompany?.data || insuranceClient?.insuranceCompany;
          const companyName = insuranceCompany?.name || 'N/A';
          
          // Format reasons
          let reasons = 'N/A';
          if (Array.isArray(attrs.reasons) && attrs.reasons.length > 0) {
            reasons = attrs.reasons.join(', ');
          } else if (attrs.description) {
            reasons = attrs.description;
          }
          
          return [
            attrs.createdAt ? new Date(attrs.createdAt).toLocaleDateString() : 'N/A',
            attrs.completedDate ? new Date(attrs.completedDate).toLocaleDateString() : 'N/A',
            attrs.requestNumber || `REQ-${item.id}`,
            vehicle,
            year?.toString() || 'N/A',
            plate,
            attrs.phoneNumber || 'N/A',
            attrs.locationText || 'N/A',
            attrs.toLocationText || attrs.toDropDown || 'N/A',
            attrs.serviceType || 'N/A',
            reasons.replace(/,/g, ';'),
            policyNumber,
            companyName,
          ];
        });
        break;
      case 'employees':
        headers = ['Employee Number', 'First Name', 'Last Name', 'Email', 'Phone', 'Department', 'Position', 'Status'];
        rows = filteredData.map((item) => {
          const attrs = item.attributes || item;
          const dept = attrs.department?.data?.attributes?.name || attrs.department?.name || 'N/A';
          return [
            attrs.employeeNumber || '',
            attrs.firstName || '',
            attrs.lastName || '',
            attrs.email || '',
            attrs.phone || '',
            dept,
            attrs.position || '',
            attrs.status || '',
          ];
        });
        break;
      case 'invoices':
        headers = ['Invoice Number', 'Request ID', 'Invoice Type', 'Invoice Date', 'Due Date', 'Total', 'Paid', 'Balance', 'Products'];
        rows = filteredData.map((item) => {
          const attrs = item.attributes || item;
          const request = attrs.request?.data?.attributes || attrs.request?.data || attrs.request;
          const requestNumber = request?.requestNumber || request?.attributes?.requestNumber || (request?.id ? `REQ-${request.id}` : '0');
          const items = invoiceItems[item.id] || [];
          const productsList = items.map((item: any) => {
            const itemAttrs = item.attributes || item;
            const product = itemAttrs.product?.data?.attributes || itemAttrs.product;
            return product?.name || itemAttrs.description || '0';
          }).join('; ');
          
          // Determine invoice type for CSV based on invoiceType field
          const dbInvoiceType = attrs.invoiceType || '';
          let displayInvoiceType = 'Client';
          if (dbInvoiceType === 'insurance_company_monthly') {
            displayInvoiceType = 'Insurance';
          } else if (dbInvoiceType === 'service_provider_monthly') {
            displayInvoiceType = 'Service Provider';
          } else if (dbInvoiceType === 'service_provider_to_darb') {
            displayInvoiceType = 'Client';
          }
          
          return [
            attrs.invoiceNumber || '0',
            requestNumber,
            displayInvoiceType,
            attrs.invoiceDate ? new Date(attrs.invoiceDate).toLocaleDateString() : '0',
            attrs.dueDate ? new Date(attrs.dueDate).toLocaleDateString() : '0',
            (attrs.total || 0).toString(),
            (attrs.paidAmount || 0).toString(),
            (attrs.balanceDue || 0).toString(),
            productsList || '0',
          ];
        });
        break;
      case 'payrolls':
        headers = ['Period', 'Employee', 'Base Salary', 'Bonuses', 'Deductions', 'Net Salary', 'Status'];
        rows = filteredData.map((item) => {
          const attrs = item.attributes || item;
          const employee = attrs.employee?.data?.attributes || attrs.employee;
          const employeeName = employee ? `${employee.firstName || ''} ${employee.lastName || ''}`.trim() : 'N/A';
          return [
            attrs.period || '',
            employeeName,
            (attrs.baseSalary || 0).toString(),
            (attrs.bonuses || 0).toString(),
            (attrs.deductions || 0).toString(),
            (attrs.netSalary || 0).toString(),
            attrs.status || '',
          ];
        });
        break;
    }

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${reportType}-report-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderFilters = () => {
    if (!reportType) return null;

    return (
      <Accordion 
        defaultExpanded 
        sx={{ 
          mb: 3,
          borderRadius: 2,
          '&:before': { display: 'none' },
          boxShadow: 2,
        }}
      >
        <AccordionSummary 
          expandIcon={<ExpandMore />}
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            borderRadius: 2,
            '&:hover': {
              bgcolor: 'primary.dark',
            },
            '& .MuiAccordionSummary-expandIconWrapper': {
              color: 'white',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <FilterList sx={{ fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Filters</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 3, pb: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 3 }}>
            {reportType === 'requests' && (
              <>
                <Box>
                  <FormControl fullWidth size="medium">
                    <InputLabel sx={{ fontWeight: 600 }}>Status</InputLabel>
                    <Select
                      value={filters.requestStatus || 'completed'}
                      label="Status"
                      onChange={(e) => handleFilterChange('requestStatus', e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                        fontWeight: 500,
                      }}
                    >
                      <MenuItem value="" sx={{ fontWeight: 700, bgcolor: 'action.hover' }}>All Statuses</MenuItem>
                      <MenuItem value="completed" sx={{ fontWeight: 700, color: 'success.main' }}>✓ Completed</MenuItem>
                      <MenuItem value="cancelled" sx={{ fontWeight: 700, color: 'error.main' }}>✗ Cancelled</MenuItem>
                      <MenuItem disabled sx={{ fontSize: '0.75rem', color: 'text.disabled', py: 0.5 }}>─────────────</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="assigned">Assigned</MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="invoice_creation">Invoice Creation</MenuItem>
                      <MenuItem value="invoice_sent">Invoice Sent</MenuItem>
                      <MenuItem value="payment_received">Payment Received</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box>
                  <FormControl fullWidth size="medium">
                    <InputLabel sx={{ fontWeight: 600 }}>Priority</InputLabel>
                    <Select
                      value={filters.requestPriority || ''}
                      label="Priority"
                      onChange={(e) => handleFilterChange('requestPriority', e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                        fontWeight: 500,
                      }}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="low">Low</MenuItem>
                      <MenuItem value="medium">Medium</MenuItem>
                      <MenuItem value="high">High</MenuItem>
                      <MenuItem value="urgent">Urgent</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box>
                  <FormControl fullWidth size="medium">
                    <InputLabel sx={{ fontWeight: 600 }}>Service Provider</InputLabel>
                    <Select
                      value={filters.requestServiceProvider || ''}
                      label="Service Provider"
                      onChange={(e) => handleFilterChange('requestServiceProvider', e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                        fontWeight: 500,
                      }}
                    >
                      <MenuItem value="">All</MenuItem>
                      {serviceProviders.map((provider) => {
                        const attrs = provider.attributes || provider;
                        return (
                          <MenuItem key={provider.id} value={String(provider.id)}>
                            {attrs.name}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Box>
                <Box>
                  <FormControl fullWidth size="medium">
                    <InputLabel sx={{ fontWeight: 600 }}>Service Type</InputLabel>
                    <Select
                      value={filters.requestServiceType || ''}
                      label="Service Type"
                      onChange={(e) => handleFilterChange('requestServiceType', e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                        fontWeight: 500,
                      }}
                    >
                      <MenuItem value="">All</MenuItem>
                      {getUniqueValues('serviceType').map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box>
                  <FormControl fullWidth size="medium">
                    <InputLabel sx={{ fontWeight: 600 }}>Insurance Company</InputLabel>
                    <Select
                      value={filters.requestInsuranceCompany || ''}
                      label="Insurance Company"
                      onChange={(e) => handleFilterChange('requestInsuranceCompany', e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                        fontWeight: 500,
                      }}
                    >
                      <MenuItem value="">All</MenuItem>
                      {insuranceCompanies.map((company) => {
                        const id = company.id || company.attributes?.id || company.data?.id;
                        const name = company.name || company.attributes?.name || company.data?.attributes?.name;
                        return (
                          <MenuItem key={id} value={String(id)}>
                            {name}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Box>
              </>
            )}

            {reportType === 'employees' && (
              <>
                <Box>
                  <FormControl fullWidth size="medium">
                    <InputLabel sx={{ fontWeight: 600 }}>Status</InputLabel>
                    <Select
                      value={filters.employeeStatus || ''}
                      label="Status"
                      onChange={(e) => handleFilterChange('employeeStatus', e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'success.main' },
                        fontWeight: 500,
                      }}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="onLeave">On Leave</MenuItem>
                      <MenuItem value="suspended">Suspended</MenuItem>
                      <MenuItem value="terminated">Terminated</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box>
                  <FormControl fullWidth size="medium">
                    <InputLabel sx={{ fontWeight: 600 }}>Department</InputLabel>
                    <Select
                      value={filters.employeeDepartment || ''}
                      label="Department"
                      onChange={(e) => handleFilterChange('employeeDepartment', e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'success.main' },
                        fontWeight: 500,
                      }}
                    >
                      <MenuItem value="">All</MenuItem>
                      {departments.map((dept) => {
                        const attrs = dept.attributes || dept;
                        return (
                          <MenuItem key={dept.id} value={String(dept.id)}>
                            {attrs.name}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Box>
                <Box>
                  <FormControl fullWidth size="medium">
                    <InputLabel sx={{ fontWeight: 600 }}>Position</InputLabel>
                    <Select
                      value={filters.employeePosition || ''}
                      label="Position"
                      onChange={(e) => handleFilterChange('employeePosition', e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'success.main' },
                        fontWeight: 500,
                      }}
                    >
                      <MenuItem value="">All</MenuItem>
                      {getUniqueValues('position').map((pos) => (
                        <MenuItem key={pos} value={pos}>
                          {pos}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box>
                  <FormControl fullWidth size="medium">
                    <InputLabel sx={{ fontWeight: 600 }}>Employment Type</InputLabel>
                    <Select
                      value={filters.employeeEmploymentType || ''}
                      label="Employment Type"
                      onChange={(e) => handleFilterChange('employeeEmploymentType', e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'success.main' },
                        fontWeight: 500,
                      }}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="fullTime">Full Time</MenuItem>
                      <MenuItem value="partTime">Part Time</MenuItem>
                      <MenuItem value="contract">Contract</MenuItem>
                      <MenuItem value="intern">Intern</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </>
            )}

            {reportType === 'invoices' && (
              <>
                <Box>
                  <FormControl fullWidth size="medium">
                    <InputLabel sx={{ fontWeight: 600 }}>Status</InputLabel>
                    <Select
                      value={filters.invoiceStatus || ''}
                      label="Status"
                      onChange={(e) => handleFilterChange('invoiceStatus', e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'warning.main' },
                        fontWeight: 500,
                      }}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="draft">Draft</MenuItem>
                      <MenuItem value="sent">Sent</MenuItem>
                      <MenuItem value="paid">Paid</MenuItem>
                      <MenuItem value="partiallyPaid">Partially Paid</MenuItem>
                      <MenuItem value="overdue">Overdue</MenuItem>
                      <MenuItem value="cancelled">Cancelled</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box>
                  <FormControl fullWidth size="medium">
                    <InputLabel sx={{ fontWeight: 600 }}>Has Extra KM</InputLabel>
                    <Select
                      value={filters.invoiceHasExtraKm || ''}
                      label="Has Extra KM"
                      onChange={(e) => handleFilterChange('invoiceHasExtraKm', e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'warning.main' },
                        fontWeight: 500,
                      }}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="true">Yes (Has Extra KM/Kilometer)</MenuItem>
                      <MenuItem value="false">No</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box>
                  <NumberTextField
                    fullWidth
                    label="Min Amount (OMR)"
                    value={filters.invoiceMinAmount || ''}
                    onChange={(e) => handleFilterChange('invoiceMinAmount', e.target.value)}
                    InputLabelProps={{ sx: { fontWeight: 600 } }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderWidth: 2 },
                        '&:hover fieldset': { borderColor: 'warning.main' },
                        fontWeight: 500,
                      },
                    }}
                  />
                </Box>
                <Box>
                  <NumberTextField
                    fullWidth
                    label="Max Amount (OMR)"
                    value={filters.invoiceMaxAmount || ''}
                    onChange={(e) => handleFilterChange('invoiceMaxAmount', e.target.value)}
                    InputLabelProps={{ sx: { fontWeight: 600 } }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderWidth: 2 },
                        '&:hover fieldset': { borderColor: 'warning.main' },
                        fontWeight: 500,
                      },
                    }}
                  />
                </Box>
              </>
            )}

            {reportType === 'payrolls' && (
              <>
                <Box>
                  <FormControl fullWidth size="medium">
                    <InputLabel sx={{ fontWeight: 600 }}>Status</InputLabel>
                    <Select
                      value={filters.payrollStatus || ''}
                      label="Status"
                      onChange={(e) => handleFilterChange('payrollStatus', e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'info.main' },
                        fontWeight: 500,
                      }}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="draft">Draft</MenuItem>
                      <MenuItem value="processed">Processed</MenuItem>
                      <MenuItem value="paid">Paid</MenuItem>
                      <MenuItem value="cancelled">Cancelled</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="Period (YYYY-MM)"
                    type="month"
                    value={filters.payrollPeriod || ''}
                    onChange={(e) => handleFilterChange('payrollPeriod', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              </>
            )}

            <Box sx={{ gridColumn: '1 / -1' }}>
              <Button variant="outlined" onClick={clearFilters}>
                Clear All Filters
              </Button>
            </Box>
          </Box>
          {/* Action Buttons */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 3, gap: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              Click "Generate Report" to apply your filters and view results
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<Assessment />}
                onClick={() => {
                  fetchReportData();
                }}
                disabled={loading || !reportType}
                sx={{
                  px: 5,
                  py: 1.8,
                  fontWeight: 700,
                  fontSize: '1.1rem',
                  borderRadius: 2,
                  textTransform: 'none',
                  boxShadow: 3,
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 5,
                  },
                  '&:disabled': {
                    bgcolor: 'action.disabledBackground',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                Generate Report
              </Button>
            <Button
              variant="outlined"
              color="secondary"
              size="large"
              onClick={() => {
                // Reset filters - for requests, keep status as 'completed' by default
                if (reportType === 'requests') {
                  setFilters({ requestStatus: 'completed' });
                } else {
                  setFilters({});
                }
                setSearchTerm('');
              }}
              sx={{
                px: 4,
                py: 1.8,
                fontWeight: 700,
                fontSize: '1rem',
                borderRadius: 2,
                borderWidth: 2,
                textTransform: 'none',
                '&:hover': {
                  borderWidth: 2,
                  transform: 'translateY(-1px)',
                  boxShadow: 2,
                },
                transition: 'all 0.2s ease',
              }}
            >
              Clear Filters
            </Button>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderCharts = () => {
    if (!chartData) return null;

    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3, mb: 4 }}>
        {chartData.statusPie && (
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Status Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={chartData.statusPie}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {chartData.statusPie.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {chartData.dailyLine && (
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Daily Trend
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData.dailyLine}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="count" stroke="#8884d8" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {chartData.deptPie && (
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Department Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={chartData.deptPie}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {chartData.deptPie.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {chartData.statusBar && (
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Status Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData.statusBar}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="value" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {chartData.revenueLine && (
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Monthly Revenue
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData.revenueLine}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(value: any) => `OMR ${value.toFixed(2)}`} />
                  <Legend />
                  <Line type="monotone" dataKey="revenue" stroke="#00C49F" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </Box>
    );
  };

  // Helper function to generate last 12 months labels
  const getLast12Months = () => {
    const months: string[] = [];
    const monthKeys: string[] = [];
    const now = new Date();
    
    for (let i = 11; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthLabel = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      months.push(monthLabel);
      monthKeys.push(monthKey);
    }
    
    return { months, monthKeys };
  };

  const renderTable = () => {
    if (!reportType || filteredData.length === 0) return null;

    const renderRequestRow = (item: any, index: number) => {
      const attrs = item.attributes || item;
      const id = item.id || index;
      const insuranceClient = attrs.insuranceClient?.data?.attributes || attrs.insuranceClient;
      
      // Get vehicle info
      const vehicle = insuranceClient 
        ? `${insuranceClient.carMake || ''} ${insuranceClient.carModel || ''}`.trim() || 'N/A'
        : 'N/A';
      
      const year = insuranceClient?.carYear || 'N/A';
      const plate = insuranceClient?.plateNumber || 'N/A';
      const policyNumber = insuranceClient?.policyNumber || 'N/A';
      
      // Get insurance company name
      const insuranceCompany = insuranceClient?.insuranceCompany?.data?.attributes || insuranceClient?.insuranceCompany?.data || insuranceClient?.insuranceCompany;
      const companyName = insuranceCompany?.name || 'N/A';
      
      // Format reasons
      let reasons = 'N/A';
      if (Array.isArray(attrs.reasons) && attrs.reasons.length > 0) {
        reasons = attrs.reasons.join(', ');
      } else if (attrs.description) {
        reasons = attrs.description;
      }

      const handleRequestClick = async () => {
        try {
          const response = await requestService.getById(Number(id));
          setViewingRequest(response.data);
          setImageErrors({ before: false, after: false });
          setRequestModalOpen(true);
        } catch (err) {
          console.error('Error loading request:', err);
          setError('Failed to load request details.');
        }
      };

      return (
        <TableRow 
          key={id}
          sx={{
            '&:nth-of-type(odd)': { bgcolor: 'action.hover' },
            '&:hover': { bgcolor: 'action.selected' },
            transition: 'background-color 0.2s ease',
          }}
        >
          <TableCell>{attrs.createdAt ? new Date(attrs.createdAt).toLocaleDateString() : 'N/A'}</TableCell>
          <TableCell>{attrs.completedDate ? new Date(attrs.completedDate).toLocaleDateString() : 'N/A'}</TableCell>
          <TableCell>
            <Chip
              label={attrs.requestNumber || `REQ-${id}`}
              size="medium"
              variant="filled"
              color="primary"
              onClick={handleRequestClick}
              icon={<Visibility />}
              sx={{ 
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '0.9rem',
                '&:hover': { 
                  backgroundColor: 'primary.dark',
                  transform: 'translateY(-1px)',
                  boxShadow: 2,
                },
                transition: 'all 0.2s ease',
              }}
            />
          </TableCell>
          <TableCell>{vehicle}</TableCell>
          <TableCell>{year}</TableCell>
          <TableCell>{plate}</TableCell>
          <TableCell>{attrs.phoneNumber || 'N/A'}</TableCell>
          <TableCell>{attrs.locationText || 'N/A'}</TableCell>
          <TableCell>{attrs.toLocationText || attrs.toDropDown || 'N/A'}</TableCell>
          <TableCell>{attrs.serviceType || 'N/A'}</TableCell>
          <TableCell sx={{ maxWidth: 200 }}>
            <Typography variant="body2" noWrap title={reasons}>
              {reasons}
            </Typography>
          </TableCell>
          <TableCell>{policyNumber}</TableCell>
          <TableCell>
            <Chip 
              label={companyName} 
              size="medium" 
              color="primary" 
              variant="filled"
              icon={<Business />}
              sx={{ 
                fontWeight: 600,
                fontSize: '0.85rem',
              }}
            />
          </TableCell>
        </TableRow>
      );
    };

    const renderEmployeeRow = (item: any, index: number) => {
      const attrs = item.attributes || item;
      const id = item.id || index;
      const dept = attrs.department?.data?.attributes || attrs.department;

      return (
        <TableRow 
          key={id}
          sx={{
            '&:nth-of-type(odd)': { bgcolor: 'action.hover' },
            '&:hover': { bgcolor: 'action.selected' },
            transition: 'background-color 0.2s ease',
          }}
        >
          <TableCell>{attrs.employeeNumber || 'N/A'}</TableCell>
          <TableCell>{`${attrs.firstName || ''} ${attrs.lastName || ''}`.trim() || 'N/A'}</TableCell>
          <TableCell>{attrs.email || 'N/A'}</TableCell>
          <TableCell>{attrs.phone || 'N/A'}</TableCell>
          <TableCell>{dept?.name || 'N/A'}</TableCell>
          <TableCell>{attrs.position || 'N/A'}</TableCell>
          <TableCell>
            <Chip
              label={attrs.status || 'N/A'}
              size="medium"
              color={attrs.status === 'active' ? 'success' : 'default'}
              sx={{ 
                fontWeight: 600,
                fontSize: '0.85rem',
              }}
            />
          </TableCell>
        </TableRow>
      );
    };

    const renderInvoiceRow = (item: any, index: number) => {
      const attrs = item.attributes || item;
      const id = item.id || index;
      const isExpanded = expandedRows.has(id);
      const request = attrs.request?.data?.attributes || attrs.request?.data || attrs.request;
      
      // DEBUG: Log the request structure
      console.log('[Reports] Invoice Row Debug:', {
        invoiceId: id,
        invoiceNumber: attrs.invoiceNumber,
        requestRaw: attrs.request,
        requestData: attrs.request?.data,
        requestAttributes: attrs.request?.data?.attributes,
        requestExtracted: request,
      });
      
      // Extract request ID from multiple possible structures - check all possible locations
      const requestId = attrs.request?.data?.id || request?.id || attrs.request?.id || (typeof attrs.request === 'object' && attrs.request !== null ? null : '0');
      const requestNumber = request?.requestNumber || request?.attributes?.requestNumber || (requestId && requestId !== '0' ? `REQ-${requestId}` : '-');
      const items = invoiceItems[id] || [];
      
      // Determine invoice type for display based on invoiceType field
      const dbInvoiceType = attrs.invoiceType;
      
      // DEBUG: Log invoice type determination
      console.log('[Reports] Invoice Type Debug:', {
        invoiceId: id,
        invoiceNumber: attrs.invoiceNumber,
        dbInvoiceType,
      });
      
      // Determine display type based on invoiceType field
      let displayInvoiceType = 'Client';
      if (dbInvoiceType === 'insurance_company_monthly') {
        displayInvoiceType = 'Insurance';
      } else if (dbInvoiceType === 'service_provider_monthly') {
        displayInvoiceType = 'Service Provider';
      } else if (dbInvoiceType === 'service_provider_to_darb') {
        displayInvoiceType = 'Client';
      }

      // DEBUG: Log extracted values
      console.log('[Reports] Request ID Extraction:', {
        invoiceId: id,
        requestId,
        requestNumber,
        hasRequest: !!request,
        requestType: typeof request,
      });

      const handleViewRequest = async () => {
        console.log('[Reports] handleViewRequest called:', { requestId, requestNumber });
        
        if (requestId && requestId !== '0' && requestId !== null && requestId !== undefined) {
          try {
            console.log('[Reports] Fetching request with ID:', requestId);
            const response = await requestService.getById(Number(requestId));
            console.log('[Reports] Request fetched successfully:', response.data);
            setViewingRequest(response.data);
            setImageErrors({ before: false, after: false });
            setRequestModalOpen(true);
            console.log('[Reports] Modal should be open now');
          } catch (err) {
            console.error('[Reports] Error loading request:', err);
            setError('Failed to load request details.');
          }
        } else {
          console.warn('[Reports] Cannot view request - invalid requestId:', requestId);
        }
      };

      const handleDownloadPDF = async () => {
        try {
          setError('');
          // Fetch invoice with full details
          const invoiceResponse = await InvoiceService.getOne(id, {
            populate: ['customer', 'insuranceClient.insuranceCompany', 'request'],
          });
          const invoice = invoiceResponse.data?.data;
          if (!invoice) {
            setError('Invoice not found.');
            return;
          }

          const invoiceData = invoice.attributes || invoice;
          const dbInvoiceType = invoiceData.invoiceType;
          
          // Determine invoice type automatically based on invoiceType field
          const invoiceType: 'customer' | 'insurance' = (dbInvoiceType === 'insurance_company_monthly') ? 'insurance' : 'customer';
          
          // DEBUG: Log invoice type determination for PDF
          console.log('[Reports] PDF Download - Invoice Type:', {
            invoiceId: id,
            invoiceNumber: invoiceData.invoiceNumber,
            dbInvoiceType,
            determinedType: invoiceType,
          });
          
          const insuranceClient = invoiceData.insuranceClient?.data?.attributes || invoiceData.insuranceClient?.data || invoiceData.insuranceClient;

          // Fetch line items
          const itemsResponse = await InvoiceService.getItems(id);
          const lineItems = itemsResponse.data?.data || [];

          const doc = new jsPDF();
          const insuranceCompany = insuranceClient?.insuranceCompany?.data?.attributes || insuranceClient?.insuranceCompany;

          // Load and add logo
          await addLogoToPDF(doc);

          // Add company name
          doc.setFontSize(16);
          doc.setTextColor(0, 0, 0);
          doc.setFont('helvetica', 'bold');
          doc.text('DARB NATIONAL SERVICE LLC', 20, 38);

          // Add invoice type header
          if (invoiceType === 'insurance') {
            doc.setFontSize(14);
            doc.setTextColor(33, 150, 243);
            doc.text('INSURANCE INVOICE', 105, 20, { align: 'center' });
          }

          // Add invoice details on the right
          doc.setFontSize(10);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(0, 0, 0);
          const formatDate = (dateString: string) => {
            if (!dateString) return '-';
            return new Date(dateString).toLocaleDateString();
          };
          doc.text(`DATE: ${formatDate(invoiceData.invoiceDate)}`, 175, invoiceType === 'insurance' ? 30 : 20, { align: 'right' });
          doc.text(`INVOICE #: ${invoiceData.invoiceNumber}`, 175, invoiceType === 'insurance' ? 37 : 27, { align: 'right' });
          doc.text(`Status: ${(invoiceData.status || 'draft').toUpperCase()}`, 175, invoiceType === 'insurance' ? 44 : 34, { align: 'right' });

          // Add company details
          doc.setFontSize(9);
          doc.setFont('helvetica', 'normal');
          let companyY = invoiceType === 'insurance' ? 58 : 48;
          doc.text('CR: 1366462', 20, companyY);
          companyY += 5;
          doc.text('P.O. Box: 1234, Muscat, Sultanate of Oman', 20, companyY);
          companyY += 5;
          doc.text('Tel: +968 1234 5678', 20, companyY);
          companyY += 5;
          doc.text('Email: info@darb.com', 20, companyY);
          if (invoiceData.omanTaxId) {
            companyY += 5;
            doc.text(`Oman Tax ID: ${invoiceData.omanTaxId}`, 20, companyY);
          }

          // Add customer/insurance client information
          let clientY = companyY + 15;
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text('Bill To:', 20, clientY);
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(10);
          clientY += 7;
          if (insuranceClient) {
            doc.text(insuranceClient.customerName || insuranceClient.companyName || insuranceClient.contactPerson || '-', 20, clientY);
            if (insuranceClient.email) {
              clientY += 5;
              doc.text(`Email: ${insuranceClient.email}`, 20, clientY);
            }
            if (insuranceClient.phoneNumber || insuranceClient.phone) {
              clientY += 5;
              doc.text(`Phone: ${insuranceClient.phoneNumber || insuranceClient.phone}`, 20, clientY);
            }
            if (insuranceClient.vinNumber) {
              clientY += 5;
              doc.text(`VIN: ${insuranceClient.vinNumber}`, 20, clientY);
            }
          }

          // Add line items table
          let tableY = clientY + 15;
          const tableData = lineItems.map((item: any) => {
            const itemAttrs = item.attributes || item;
            const product = itemAttrs.product?.data?.attributes || itemAttrs.product;
            return [
              product?.name || itemAttrs.description || '-',
              itemAttrs.quantity || 0,
              `OMR ${Number(itemAttrs.unitPrice || 0).toFixed(2)}`,
              `OMR ${Number(itemAttrs.discount || 0).toFixed(2)}`,
              `OMR ${Number(itemAttrs.tax || 0).toFixed(2)}`,
              `OMR ${Number(itemAttrs.total || 0).toFixed(2)}`,
            ];
          });

          autoTable(doc, {
            startY: tableY,
            head: [['Description', 'Qty', 'Unit Price', 'Discount', 'Tax', 'Total']],
            body: tableData,
            theme: 'striped',
            headStyles: { fillColor: [33, 150, 243] },
          });

          // Add totals
          const finalY = (doc as any).lastAutoTable.finalY + 10;
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.text(`Subtotal: OMR ${Number(invoiceData.subtotal || invoiceData.total || 0).toFixed(2)}`, 150, finalY, { align: 'right' });
          doc.text(`Tax: OMR ${Number(invoiceData.tax || 0).toFixed(2)}`, 150, finalY + 5, { align: 'right' });
          doc.text(`Total: OMR ${Number(invoiceData.total || 0).toFixed(2)}`, 150, finalY + 10, { align: 'right' });

          // Save PDF
          doc.save(`Invoice-${invoiceData.invoiceNumber}-${invoiceType}.pdf`);
        } catch (err) {
          console.error('Error downloading invoice PDF:', err);
          setError('Failed to download invoice PDF.');
        }
      };

      return (
        <React.Fragment key={id}>
          <TableRow
            sx={{
              '&:nth-of-type(4n+1)': { bgcolor: 'action.hover' },
              '&:hover': { bgcolor: 'action.selected' },
              transition: 'background-color 0.2s ease',
            }}
          >
            <TableCell>
              <IconButton 
                size="medium" 
                onClick={() => toggleRowExpand(id)}
                sx={{
                  bgcolor: isExpanded ? 'primary.main' : 'primary.50',
                  color: isExpanded ? 'white' : 'primary.main',
                  '&:hover': {
                    bgcolor: isExpanded ? 'primary.dark' : 'primary.100',
                    transform: 'scale(1.1)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                {isExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </TableCell>
            <TableCell>{attrs.invoiceNumber || '0'}</TableCell>
            <TableCell>
              {(() => {
                const isValidRequestId = requestId && requestId !== '0' && requestId !== null && requestId !== undefined && requestId !== '-';
                
                // DEBUG: Log cell rendering
                console.log('[Reports] Rendering Request ID cell:', {
                  invoiceId: id,
                  requestId,
                  requestNumber,
                  isValidRequestId,
                  willBeClickable: isValidRequestId,
                });
                
                if (isValidRequestId) {
                  return (
                    <Typography
                      component="span"
                      onClick={(e) => {
                        console.log('[Reports] Request ID clicked:', { requestId, requestNumber });
                        e.stopPropagation();
                        handleViewRequest();
                      }}
                      sx={{
                        color: 'primary.main',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        '&:hover': {
                          color: 'primary.dark',
                        },
                      }}
                    >
                      {requestNumber}
                    </Typography>
                  );
                }
                return <span>{requestNumber}</span>;
              })()}
            </TableCell>
            <TableCell>
              <Chip
                label={displayInvoiceType}
                size="medium"
                color={displayInvoiceType === 'Insurance' ? 'primary' : displayInvoiceType === 'Service Provider' ? 'secondary' : 'warning'}
                sx={{ 
                  fontWeight: 600,
                  fontSize: '0.85rem',
                }}
              />
            </TableCell>
            <TableCell>{attrs.invoiceDate ? new Date(attrs.invoiceDate).toLocaleDateString() : '0'}</TableCell>
            <TableCell>{attrs.dueDate ? new Date(attrs.dueDate).toLocaleDateString() : '0'}</TableCell>
            <TableCell>{attrs.total ? `OMR ${Number(attrs.total).toFixed(2)}` : 'OMR 0.00'}</TableCell>
            <TableCell>{attrs.paidAmount ? `OMR ${Number(attrs.paidAmount).toFixed(2)}` : 'OMR 0.00'}</TableCell>
            <TableCell>{attrs.balanceDue ? `OMR ${Number(attrs.balanceDue).toFixed(2)}` : 'OMR 0.00'}</TableCell>
            <TableCell>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
              {requestId && requestId !== '0' ? (
                <Button
                  size="medium"
                  variant="contained"
                  color="info"
                  startIcon={<Visibility />}
                  onClick={handleViewRequest}
                  sx={{
                    fontWeight: 600,
                    px: 2,
                    textTransform: 'none',
                    boxShadow: 2,
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: 3,
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  View
                </Button>
              ) : null}
                <Button
                  size="medium"
                  variant="contained"
                  color="error"
                  startIcon={<PictureAsPdf />}
                  onClick={handleDownloadPDF}
                  sx={{
                    fontWeight: 600,
                    px: 2,
                    textTransform: 'none',
                    boxShadow: 2,
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: 3,
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  PDF
                </Button>
              </Stack>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <Box sx={{ margin: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Products
                  </Typography>
                  {items.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Product/Description</TableCell>
                            <TableCell align="right">Quantity</TableCell>
                            <TableCell align="right">Unit Price</TableCell>
                            <TableCell align="right">Discount</TableCell>
                            <TableCell align="right">Tax</TableCell>
                            <TableCell align="right">Total</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {items.map((item: any) => {
                            const itemAttrs = item.attributes || item;
                            const product = itemAttrs.product?.data?.attributes || itemAttrs.product;
                            const productName = product?.name || itemAttrs.description || '0';
                            return (
                              <TableRow key={item.id}>
                                <TableCell>{productName}</TableCell>
                                <TableCell align="right">{itemAttrs.quantity || 0}</TableCell>
                                <TableCell align="right">{itemAttrs.unitPrice ? `OMR ${Number(itemAttrs.unitPrice).toFixed(2)}` : 'OMR 0.00'}</TableCell>
                                <TableCell align="right">{itemAttrs.discount ? `OMR ${Number(itemAttrs.discount).toFixed(2)}` : 'OMR 0.00'}</TableCell>
                                <TableCell align="right">{itemAttrs.tax ? `OMR ${Number(itemAttrs.tax).toFixed(2)}` : 'OMR 0.00'}</TableCell>
                                <TableCell align="right">{itemAttrs.total ? `OMR ${Number(itemAttrs.total).toFixed(2)}` : 'OMR 0.00'}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      No products found
                    </Typography>
                  )}
                </Box>
              </Collapse>
            </TableCell>
          </TableRow>
        </React.Fragment>
      );
    };

    const renderPayrollRow = (item: any, index: number) => {
      const attrs = item.attributes || item;
      const id = item.id || index;
      const employee = attrs.employee?.data?.attributes || attrs.employee;
      const employeeName = employee ? `${employee.firstName || ''} ${employee.lastName || ''}`.trim() : 'N/A';

      return (
        <TableRow 
          key={id}
          sx={{
            '&:nth-of-type(odd)': { bgcolor: 'action.hover' },
            '&:hover': { bgcolor: 'action.selected' },
            transition: 'background-color 0.2s ease',
          }}
        >
          <TableCell>{attrs.period || 'N/A'}</TableCell>
          <TableCell>{employeeName}</TableCell>
          <TableCell>{attrs.baseSalary ? `OMR ${Number(attrs.baseSalary).toFixed(2)}` : 'N/A'}</TableCell>
          <TableCell>{attrs.bonuses ? `OMR ${Number(attrs.bonuses).toFixed(2)}` : 'N/A'}</TableCell>
          <TableCell>{attrs.deductions ? `OMR ${Number(attrs.deductions).toFixed(2)}` : 'N/A'}</TableCell>
          <TableCell>{attrs.netSalary ? `OMR ${Number(attrs.netSalary).toFixed(2)}` : 'N/A'}</TableCell>
          <TableCell>
            <Chip
              label={attrs.status || 'N/A'}
              size="medium"
              color={
                attrs.status === 'paid'
                  ? 'success'
                  : attrs.status === 'processed'
                  ? 'info'
                  : attrs.status === 'cancelled'
                  ? 'error'
                  : 'default'
              }
              sx={{ 
                fontWeight: 600,
                fontSize: '0.85rem',
              }}
            />
          </TableCell>
        </TableRow>
      );
    };

    const getTableHeaders = () => {
      switch (reportType) {
        case 'requests':
          return [
            { field: 'createdAt', label: 'Created At', sortable: true },
            { field: 'completedDate', label: 'Complete Date', sortable: true },
            { field: 'requestNumber', label: 'Request ID', sortable: true },
            { field: 'vehicle', label: 'Vehicle', sortable: false },
            { field: 'year', label: 'Year', sortable: false },
            { field: 'plate', label: 'Plate', sortable: false },
            { field: 'phoneNumber', label: 'Phone', sortable: false },
            { field: 'locationText', label: 'From', sortable: false },
            { field: 'toLocationText', label: 'To', sortable: false },
            { field: 'serviceType', label: 'Service Type', sortable: true },
            { field: 'reason', label: 'Reason', sortable: false },
            { field: 'policyNumber', label: 'Policy Number', sortable: false },
            { field: 'company', label: 'Company', sortable: false },
          ];
        case 'employees':
          return [
            { field: 'employeeNumber', label: 'Employee #', sortable: true },
            { field: 'firstName', label: 'Name', sortable: true },
            { field: 'email', label: 'Email', sortable: true },
            { field: 'phone', label: 'Phone', sortable: true },
            { field: 'department', label: 'Department', sortable: true },
            { field: 'position', label: 'Position', sortable: true },
            { field: 'status', label: 'Status', sortable: true },
          ];
        case 'invoices':
          return [
            { field: '', label: '', sortable: false },
            { field: 'invoiceNumber', label: 'Invoice #', sortable: true },
            { field: 'request', label: 'Request ID', sortable: true },
            { field: 'invoiceType', label: 'Invoice Type', sortable: true },
            { field: 'invoiceDate', label: 'Invoice Date', sortable: true },
            { field: 'dueDate', label: 'Due Date', sortable: true },
            { field: 'total', label: 'Total', sortable: true },
            { field: 'paidAmount', label: 'Paid', sortable: true },
            { field: 'balanceDue', label: 'Balance', sortable: true },
            { field: '', label: 'Actions', sortable: false },
          ];
        case 'payrolls':
          return [
            { field: 'period', label: 'Period', sortable: true },
            { field: 'employee', label: 'Employee', sortable: true },
            { field: 'baseSalary', label: 'Base Salary', sortable: true },
            { field: 'bonuses', label: 'Bonuses', sortable: true },
            { field: 'deductions', label: 'Deductions', sortable: true },
            { field: 'netSalary', label: 'Net Salary', sortable: true },
            { field: 'status', label: 'Status', sortable: true },
          ];
        default:
          return [];
      }
    };

    const headers = getTableHeaders();

    return (
      <Card 
        elevation={3}
        sx={{ 
          borderRadius: 3,
          overflow: 'hidden',
          border: 2,
          borderColor: 'divider',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                {reportType === 'requests' && 'Service Requests'}
                {reportType === 'employees' && 'Employees'}
                {reportType === 'invoices' && 'Invoices'}
                {reportType === 'payrolls' && 'Payrolls'}
              </Typography>
              <Chip 
                label={`${filteredData.length} records`}
                color="primary"
                size="medium"
                sx={{ fontWeight: 600 }}
              />
            </Box>
            <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 1 }}>
              {reportType === 'requests' && (
                <Button
                  variant="contained"
                  color="error"
                  size="large"
                  startIcon={<PictureAsPdf />}
                  onClick={handleDownloadPDF}
                  disabled={filteredData.length === 0}
                  sx={{
                    py: 1.5,
                    px: 3,
                    fontSize: '1rem',
                    fontWeight: 700,
                    borderRadius: 2,
                    textTransform: 'none',
                    boxShadow: 3,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 5,
                    },
                    '&:disabled': {
                      bgcolor: 'action.disabledBackground',
                    },
                  }}
                >
                  Export PDF
                </Button>
              )}
              <Button
                variant="contained"
                color="success"
                size="large"
                startIcon={<FileDownload />}
                onClick={exportToCSV}
                disabled={filteredData.length === 0}
                sx={{
                  py: 1.5,
                  px: 3,
                  fontSize: '1rem',
                  fontWeight: 700,
                  borderRadius: 2,
                  textTransform: 'none',
                  boxShadow: 3,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 5,
                  },
                  '&:disabled': {
                    bgcolor: 'action.disabledBackground',
                  },
                }}
              >
                Export Excel
              </Button>
            </Stack>
          </Box>

          <TextField
            fullWidth
            placeholder="Search records..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ 
              mb: 3,
              '& .MuiOutlinedInput-root': {
                fontSize: '1.1rem',
                '& fieldset': { borderWidth: 2 },
                '&:hover fieldset': { borderColor: 'primary.main' },
                '&.Mui-focused fieldset': { borderWidth: 2.5 },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 28, color: 'primary.main' }} />
                </InputAdornment>
              ),
            }}
          />

          <TableContainer component={Paper} elevation={0} sx={{ border: 2, borderColor: 'divider', borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ 
                  bgcolor: 'primary.main',
                  '& .MuiTableCell-head': { 
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    py: 2,
                  },
                }}>
                  {headers.map((header) => (
                    <TableCell key={header.field}>
                      {header.sortable ? (
                        <TableSortLabel
                          active={sortField === header.field}
                          direction={sortField === header.field ? sortDirection : 'asc'}
                          onClick={() => handleSort(header.field)}
                        >
                          {header.label}
                        </TableSortLabel>
                      ) : (
                        header.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={20} sx={{ py: 8, textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <Search sx={{ fontSize: 64, color: 'text.disabled' }} />
                        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600 }}>
                          No records found
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Try adjusting your filters or search term
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredData.map((item, index) => {
                    switch (reportType) {
                      case 'requests':
                        return renderRequestRow(item, index);
                      case 'employees':
                        return renderEmployeeRow(item, index);
                      case 'invoices':
                        return renderInvoiceRow(item, index);
                      case 'payrolls':
                        return renderPayrollRow(item, index);
                      default:
                        return null;
                    }
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    );
  };

  return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: 'background.default', minHeight: '100vh' }}>
        <Box sx={{ 
          mb: 4, 
          pb: 3, 
          borderBottom: 3, 
          borderColor: 'primary.main',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}>
          <Assessment sx={{ fontSize: 48, color: 'primary.main' }} />
          <Box>
            <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main' }}>
              {t('reports.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
              Generate and export comprehensive reports
            </Typography>
          </Box>
        </Box>

        {/* Report Type and Date Range */}
        <Card 
          elevation={3} 
          sx={{ 
            mb: 4,
            borderRadius: 3,
            overflow: 'hidden',
            border: 2,
            borderColor: 'divider',
          }}
        >
          <CardContent>
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ mb: 3, fontWeight: 600, fontSize: '1.1rem' }}>
                {t('reports.reportType')}
              </Typography>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                gap: 2
              }}>
                <Button
                  variant={reportType === 'requests' ? 'contained' : 'outlined'}
                  startIcon={<Description />}
                  onClick={() => {
                    setReportType('requests');
                    setInvoiceSubType('all');
                    setFilters({ requestStatus: 'completed' });
                    setSearchTerm('');
                  }}
                  sx={{
                    py: 2,
                    fontSize: '1rem',
                    fontWeight: 600,
                    borderRadius: 2,
                    borderWidth: 2,
                    transition: 'all 0.2s ease',
                    bgcolor: reportType === 'requests' ? 'primary.main' : 'transparent',
                    color: reportType === 'requests' ? 'white' : 'primary.main',
                    '&:hover': {
                      borderWidth: 2,
                      transform: 'translateY(-2px)',
                      boxShadow: reportType === 'requests' ? 4 : 2,
                      bgcolor: reportType === 'requests' ? 'primary.dark' : 'rgba(25, 118, 210, 0.04)',
                    },
                  }}
                >
                  Service Requests
                </Button>
                <Button
                  variant={reportType === 'employees' ? 'contained' : 'outlined'}
                  startIcon={<People />}
                  onClick={() => {
                    setReportType('employees');
                    setInvoiceSubType('all');
                    setFilters({});
                    setSearchTerm('');
                  }}
                  sx={{
                    py: 2,
                    fontSize: '1rem',
                    fontWeight: 600,
                    borderRadius: 2,
                    borderWidth: 2,
                    transition: 'all 0.2s ease',
                    bgcolor: reportType === 'employees' ? 'success.main' : 'transparent',
                    borderColor: reportType === 'employees' ? 'success.main' : 'success.main',
                    color: reportType === 'employees' ? 'white' : 'success.main',
                    '&:hover': {
                      borderWidth: 2,
                      borderColor: 'success.main',
                      transform: 'translateY(-2px)',
                      boxShadow: reportType === 'employees' ? 4 : 2,
                      bgcolor: reportType === 'employees' ? 'success.dark' : 'rgba(46, 125, 50, 0.04)',
                    },
                  }}
                >
                  Employees
                </Button>
                <Button
                  variant={reportType === 'invoices' ? 'contained' : 'outlined'}
                  startIcon={<Receipt />}
                  onClick={() => {
                    setReportType('invoices');
                    setInvoiceSubType('all');
                    setFilters({});
                    setSearchTerm('');
                  }}
                  sx={{
                    py: 2,
                    fontSize: '1rem',
                    fontWeight: 600,
                    borderRadius: 2,
                    borderWidth: 2,
                    transition: 'all 0.2s ease',
                    bgcolor: reportType === 'invoices' ? 'warning.main' : 'transparent',
                    borderColor: reportType === 'invoices' ? 'warning.main' : 'warning.main',
                    color: reportType === 'invoices' ? 'white' : 'warning.main',
                    '&:hover': {
                      borderWidth: 2,
                      borderColor: 'warning.main',
                      transform: 'translateY(-2px)',
                      boxShadow: reportType === 'invoices' ? 4 : 2,
                      bgcolor: reportType === 'invoices' ? 'warning.dark' : 'rgba(237, 108, 2, 0.04)',
                    },
                  }}
                >
                  Invoices
                </Button>
                <Button
                  variant={reportType === 'payrolls' ? 'contained' : 'outlined'}
                  startIcon={<Payment />}
                  onClick={() => {
                    setReportType('payrolls');
                    setInvoiceSubType('all');
                    setFilters({});
                    setSearchTerm('');
                  }}
                  sx={{
                    py: 2,
                    fontSize: '1rem',
                    fontWeight: 600,
                    borderRadius: 2,
                    borderWidth: 2,
                    transition: 'all 0.2s ease',
                    bgcolor: reportType === 'payrolls' ? 'info.main' : 'transparent',
                    borderColor: reportType === 'payrolls' ? 'info.main' : 'info.main',
                    color: reportType === 'payrolls' ? 'white' : 'info.main',
                    '&:hover': {
                      borderWidth: 2,
                      borderColor: 'info.main',
                      transform: 'translateY(-2px)',
                      boxShadow: reportType === 'payrolls' ? 4 : 2,
                      bgcolor: reportType === 'payrolls' ? 'info.dark' : 'rgba(2, 136, 209, 0.04)',
                    },
                  }}
                >
                  Payrolls
                </Button>
              </Box>
            </Box>

            {/* Invoice Sub-Type Selection */}
            {reportType === 'invoices' && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                  Invoice Type
                </Typography>
                <ToggleButtonGroup
                  value={invoiceSubType}
                  exclusive
                  onChange={(e, newValue) => {
                    if (newValue !== null) {
                      setInvoiceSubType(newValue);
                      // Clear insurance service charge filter when switching types
                      if (newValue !== 'insurance') {
                        handleFilterChange('invoiceHasInsuranceServiceCharge', '');
                      }
                    }
                  }}
                  aria-label="invoice sub-type selection"
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    '& .MuiToggleButton-root': {
                      px: 3,
                      py: 1.5,
                      fontSize: '0.95rem',
                      fontWeight: 600,
                      borderRadius: 2,
                      borderWidth: 2,
                      transition: 'all 0.2s ease',
                      '&.Mui-selected': {
                        bgcolor: 'warning.main',
                        color: 'white',
                        borderColor: 'warning.main',
                        '&:hover': {
                          bgcolor: 'warning.dark',
                        },
                      },
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: 1,
                      },
                    },
                  }}
                >
                  <ToggleButton value="all" aria-label="all invoices">
                    All Invoices
                  </ToggleButton>
                  <ToggleButton value="user" aria-label="user invoices">
                    User Invoices (Extra KM)
                  </ToggleButton>
                  <ToggleButton value="insurance" aria-label="insurance invoices">
                    Insurance Invoices
                  </ToggleButton>
                </ToggleButtonGroup>
                
                {/* Insurance Service Charge Filter */}
                {invoiceSubType === 'insurance' && (
                  <Box sx={{ mt: 3 }}>
                    <Chip
                      label="Insurance service charge"
                      onClick={() => {
                        const currentFilter = filters.invoiceHasInsuranceServiceCharge;
                        handleFilterChange('invoiceHasInsuranceServiceCharge', currentFilter === 'true' ? '' : 'true');
                      }}
                      color={filters.invoiceHasInsuranceServiceCharge === 'true' ? 'warning' : 'default'}
                      variant={filters.invoiceHasInsuranceServiceCharge === 'true' ? 'filled' : 'outlined'}
                      sx={{ 
                        cursor: 'pointer', 
                        fontSize: '0.9rem',
                        py: 2.5,
                        px: 2,
                        fontWeight: 600,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: 'translateY(-1px)',
                          boxShadow: 2,
                        },
                      }}
                    />
                  </Box>
                )}
              </Box>
            )}

            {reportType === 'requests' ? (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                  Step 1: Select Month
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <IconButton 
                      onClick={() => {
                        setSelectedMonth((prev) => {
                          let newMonth = prev.month - 1;
                          let newYear = prev.year;
                          if (newMonth < 1) {
                            newMonth = 12;
                            newYear -= 1;
                          }
                          return { month: newMonth, year: newYear };
                        });
                      }}
                      disabled={loading}
                      sx={{
                        bgcolor: 'primary.main',
                        color: 'white',
                        '&:hover': {
                          bgcolor: 'primary.dark',
                          transform: 'scale(1.05)',
                        },
                        '&:disabled': {
                          bgcolor: 'action.disabledBackground',
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <ArrowBack />
                    </IconButton>
                    <FormControl sx={{ minWidth: 250 }}>
                      <InputLabel sx={{ fontWeight: 600 }}>Select Month</InputLabel>
                      <Select
                        value={`${selectedMonth.year}-${String(selectedMonth.month).padStart(2, '0')}`}
                        label="Select Month"
                        onChange={(e) => {
                          const [year, month] = e.target.value.split('-');
                          setSelectedMonth({ month: parseInt(month), year: parseInt(year) });
                        }}
                        sx={{
                          fontWeight: 600,
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderWidth: 2,
                          },
                        }}
                      >
                        {Array.from({ length: 24 }, (_, i) => {
                          const date = new Date();
                          date.setMonth(date.getMonth() - 12 + i);
                          const year = date.getFullYear();
                          const month = date.getMonth() + 1;
                          const monthName = date.toLocaleString('default', { month: 'long', year: 'numeric' });
                          return (
                            <MenuItem 
                              key={`${year}-${String(month).padStart(2, '0')}`} 
                              value={`${year}-${String(month).padStart(2, '0')}`}
                              sx={{ fontWeight: 500 }}
                            >
                              {monthName}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                    <IconButton 
                      onClick={() => {
                        setSelectedMonth((prev) => {
                          let newMonth = prev.month + 1;
                          let newYear = prev.year;
                          if (newMonth > 12) {
                            newMonth = 1;
                            newYear += 1;
                          }
                          return { month: newMonth, year: newYear };
                        });
                      }}
                      disabled={loading}
                      sx={{
                        bgcolor: 'primary.main',
                        color: 'white',
                        '&:hover': {
                          bgcolor: 'primary.dark',
                          transform: 'scale(1.05)',
                        },
                        '&:disabled': {
                          bgcolor: 'action.disabledBackground',
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <ArrowForward />
                    </IconButton>
                  </Stack>
                  <Chip
                    label={new Date(selectedMonth.year, selectedMonth.month - 1).toLocaleString('default', { month: 'long', year: 'numeric' })}
                    color="primary"
                    sx={{ 
                      fontWeight: 700,
                      fontSize: '1rem',
                      py: 2.5,
                      px: 1,
                    }}
                  />
                </Box>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                  Step 2: Select Insurance Company (Optional)
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <FormControl sx={{ minWidth: 300 }} size="medium">
                    <InputLabel sx={{ fontWeight: 600 }}>Insurance Company (Optional)</InputLabel>
                    <Select
                      value={filters.requestInsuranceCompany || ''}
                      label="Insurance Company (Optional)"
                      onChange={(e) => handleFilterChange('requestInsuranceCompany', e.target.value)}
                      disabled={loading || data.length === 0}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                        fontWeight: 500,
                      }}
                    >
                      <MenuItem value="">All Companies</MenuItem>
                      {insuranceCompanies.map((company) => {
                        const id = company.id || company.attributes?.id || company.data?.id;
                        const name = company.name || company.attributes?.name || company.data?.attributes?.name;
                        return (
                          <MenuItem key={id} value={String(id)}>
                            {name}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Data will load automatically after selecting month. You can filter by company after data loads.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 3 }}>
                <Box>
                  <TextField
                    fullWidth
                    label="Start Date"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ 
                      shrink: true,
                      sx: { fontWeight: 600, fontSize: '1rem' }
                    }}
                    disabled={!reportType}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '1rem',
                        '& fieldset': { borderWidth: 2 },
                        '&:hover fieldset': { borderColor: 'primary.main' },
                        fontWeight: 500,
                      },
                    }}
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="End Date"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ 
                      shrink: true,
                      sx: { fontWeight: 600, fontSize: '1rem' }
                    }}
                    disabled={!reportType}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '1rem',
                        '& fieldset': { borderWidth: 2 },
                        '&:hover fieldset': { borderColor: 'primary.main' },
                        fontWeight: 500,
                      },
                    }}
                  />
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Card elevation={2} sx={{ my: 4, bgcolor: 'primary.50' }}>
            <CardContent>
              {reportType === 'requests' && loadingProgress > 0 ? (
                <Box sx={{ width: '100%', maxWidth: 700, mx: 'auto', py: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" color="primary.main" sx={{ fontWeight: 600 }}>
                      Loading service requests...
                    </Typography>
                    <Chip 
                      label={`${loadingProgress}%`}
                      color="primary"
                      sx={{ fontWeight: 700, fontSize: '1rem' }}
                    />
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={loadingProgress} 
                    sx={{ 
                      height: 12, 
                      borderRadius: 6,
                      bgcolor: 'primary.100',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 6,
                      },
                    }}
                  />
                  <Typography variant="body1" color="text.secondary" sx={{ mt: 1.5, display: 'block', textAlign: 'center', fontWeight: 500 }}>
                    Fetching data pages... Please wait
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                  <CircularProgress size={60} thickness={4} />
                  <Typography variant="h6" sx={{ mt: 3, fontWeight: 600, color: 'primary.main' }}>
                    Loading data...
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {!loading && reportType && (
          <>
            {renderFilters()}
            {renderCharts()}
            {renderTable()}
          </>
        )}

        {!loading && !reportType && (
          <Card 
            elevation={3}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
            }}
          >
            <CardContent sx={{ py: 8, textAlign: 'center' }}>
              <Assessment sx={{ fontSize: 80, mb: 2, opacity: 0.9 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
                Select a Report Type
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400 }}>
                Choose from Service Requests, Employees, Invoices, or Payrolls above to view data
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* Request Details Modal */}
        <Dialog open={requestModalOpen} onClose={() => setRequestModalOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6">Request Details</Typography>
              {viewingRequest && (
                <Chip
                  label={viewingRequest.requestNumber || viewingRequest.attributes?.requestNumber || `REQ-${viewingRequest.id}`}
                  color="primary"
                  size="small"
                />
              )}
            </Box>
          </DialogTitle>
          <DialogContent>
            {(() => {
              console.log('[Reports] Modal DialogContent rendering:', {
                requestModalOpen,
                hasViewingRequest: !!viewingRequest,
                viewingRequest,
              });
              
              if (!viewingRequest) {
                return <Typography>Loading request details...</Typography>;
              }
              
              const attrs = viewingRequest.attributes || viewingRequest;
              const insuranceClient = attrs.insuranceClient?.data?.attributes || attrs.insuranceClient?.data || attrs.insuranceClient;
              const customer = attrs.customer?.data?.attributes || attrs.customer?.data || attrs.customer;
              const serviceProvider = attrs.serviceProvider?.data?.attributes || attrs.serviceProvider?.data || attrs.serviceProvider;
              
              console.log('[Reports] Modal data extracted:', {
                attrs,
                insuranceClient,
                customer,
                serviceProvider,
              });
              
              return (
                <Stack spacing={3} sx={{ mt: 1 }}>
                  {/* Request Information */}
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                      Request Information
                    </Typography>
                    <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
                    Request Number
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                          {attrs.requestNumber || `REQ-${viewingRequest.id}`}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
                    Status
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                            label={(attrs.status || 'pending').replace('_', ' ')}
                      color={
                              attrs.status === 'completed' ? 'success' :
                              attrs.status === 'cancelled' ? 'error' :
                              attrs.status === 'pending' ? 'warning' : 'info'
                      }
                      size="small"
                    />
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
                    Reason (user requested service)
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                          {attrs.description || '—'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
                    Reason (service provider report)
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                          {Array.isArray(attrs.reasons) && attrs.reasons.length > 0
                            ? attrs.reasons.join(', ')
                            : '—'}
                  </Typography>
                </Box>
                      {attrs.serviceType && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
                            Service Type
                          </Typography>
                          <Typography variant="body1" sx={{ mt: 0.5 }}>
                            {attrs.serviceType}
                          </Typography>
                        </Box>
                      )}
                      {serviceProvider && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
                            Service Provider
                          </Typography>
                          <Typography variant="body1" sx={{ mt: 0.5 }}>
                            {(() => {
                              const providerId = attrs.serviceProvider?.data?.id || attrs.serviceProvider?.id;
                              const providerName = serviceProvider.name || 'N/A';
                              
                              if (providerId && providerName !== 'N/A') {
                                return (
                                  <Link
                                    component="button"
                                    variant="body1"
                                    onClick={() => {
                                      navigate(`/service-providers/${providerId}/overview`);
                                    }}
                                    sx={{
                                      textDecoration: 'none',
                                      cursor: 'pointer',
                                      color: 'primary.main',
                                      '&:hover': {
                                        textDecoration: 'underline',
                                      },
                                    }}
                                  >
                                    {providerName}
                                  </Link>
                                );
                              }
                              return <span>{providerName}</span>;
                            })()}
                          </Typography>
                        </Box>
                      )}
                      {attrs.phoneNumber && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
                      Phone Number
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                            {attrs.phoneNumber}
                    </Typography>
                  </Box>
                      )}
                      {attrs.scheduledDate && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
                      Scheduled Date
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                            {new Date(attrs.scheduledDate).toLocaleString()}
                    </Typography>
                  </Box>
                      )}
                      {attrs.completedDate && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
                      Completed Date
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                            {new Date(attrs.completedDate).toLocaleString()}
                    </Typography>
                  </Box>
                      )}
              </Stack>
                  </Box>

                  {/* Client Information */}
                  {(insuranceClient || customer) && (
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                        Client Information
                      </Typography>
                      {insuranceClient ? (
                        <Stack spacing={2}>
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
                              Client Name
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                              {(() => {
                                const clientId = attrs.insuranceClient?.data?.id || attrs.insuranceClient?.id;
                                const clientName = insuranceClient.customerName || insuranceClient.companyName || insuranceClient.contactPerson || 'N/A';
                                
                                if (clientId && clientName !== 'N/A') {
                                  return (
                                    <Link
                                      component="button"
                                      variant="body1"
                                      onClick={() => {
                                        navigate(`/insurance-clients/${clientId}/overview`);
                                      }}
                                      sx={{
                                        textDecoration: 'none',
                                        cursor: 'pointer',
                                        color: 'primary.main',
                                        fontWeight: 500,
                                        '&:hover': {
                                          textDecoration: 'underline',
                                        },
                                      }}
                                    >
                                      {clientName}
                                    </Link>
                                  );
                                }
                                return <span>{clientName}</span>;
                              })()}
                            </Typography>
                          </Box>
                          {insuranceClient.vinNumber && (
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
                                VIN Number
                              </Typography>
                              <Typography variant="body1" sx={{ mt: 0.5 }}>
                                {insuranceClient.vinNumber}
                              </Typography>
                            </Box>
                          )}
                          {insuranceClient.phoneNumber && (
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
                                Phone Number
                              </Typography>
                              <Typography variant="body1" sx={{ mt: 0.5 }}>
                                {insuranceClient.phoneNumber}
                              </Typography>
                            </Box>
                          )}
                          {insuranceClient.email && (
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
                                Email
                              </Typography>
                              <Typography variant="body1" sx={{ mt: 0.5 }}>
                                {insuranceClient.email}
                              </Typography>
                            </Box>
                          )}
                          {(insuranceClient.carMake || insuranceClient.carModel) && (
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
                                Vehicle
                              </Typography>
                              <Typography variant="body1" sx={{ mt: 0.5 }}>
                                {insuranceClient.carMake || ''} {insuranceClient.carModel || ''} {insuranceClient.carYear ? `(${insuranceClient.carYear})` : ''} {insuranceClient.plateNumber ? `- ${insuranceClient.plateNumber}` : ''}
                              </Typography>
                            </Box>
                          )}
                          {insuranceClient.insuranceCompany && (
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
                                Insurance Company
                              </Typography>
                              <Typography variant="body1" sx={{ mt: 0.5 }}>
                                {insuranceClient.insuranceCompany?.data?.attributes?.name || insuranceClient.insuranceCompany?.attributes?.name || insuranceClient.insuranceCompany?.name || 'N/A'}
                              </Typography>
                            </Box>
                          )}
                        </Stack>
                      ) : customer ? (
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
                            Customer Name
                          </Typography>
                          <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                            {(() => {
                              const customerId = attrs.customer?.data?.id || attrs.customer?.id;
                              const customerName = customer.name || customer.companyName || customer.contactPerson || customer.firstName ? `${customer.firstName || ''} ${customer.lastName || ''}`.trim() : 'N/A';
                              
                              // Note: Regular customers might not have an overview page, so we'll just show the name
                              // If you have a customer overview route, you can add navigation here
                              return <span>{customerName}</span>;
                            })()}
                          </Typography>
                        </Box>
                          {customer.phone && (
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
                                Phone Number
                              </Typography>
                              <Typography variant="body1" sx={{ mt: 0.5 }}>
                                {customer.phone}
                              </Typography>
                            </Box>
                          )}
                          {customer.email && (
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
                                Email
                              </Typography>
                              <Typography variant="body1" sx={{ mt: 0.5 }}>
                                {customer.email}
                              </Typography>
                            </Box>
                          )}
                        </Stack>
                      ) : null}
                    </Box>
                  )}

                  {/* Evidence Photos */}
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                      Evidence Photos
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                          Before
                        </Typography>
                        {(() => {
                          const beforePhoto = attrs.evidencePhotoBefore?.data || attrs.evidencePhotoBefore;
                          const imageUrl = getImageUrl(beforePhoto);
                          const hasImage = hasValidImage(beforePhoto) && !imageErrors.before && imageUrl;
                          
                          return hasImage ? (
                            <Box>
                              <img
                                src={imageUrl}
                                alt="Before"
                                style={{ maxWidth: '300px', maxHeight: '300px', borderRadius: '4px', border: '1px solid #ddd' }}
                                onError={() => setImageErrors(prev => ({ ...prev, before: true }))}
                              />
                            </Box>
                          ) : (
                            <Box
                              sx={{
                                width: '300px',
                                height: '300px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '4px',
                                border: '1px solid #ddd',
                                backgroundColor: '#f5f5f5',
                                color: 'text.secondary'
                              }}
                            >
                              <Typography variant="body2">No image</Typography>
                            </Box>
                          );
                        })()}
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                          After
                        </Typography>
                        {(() => {
                          const afterPhoto = attrs.evidencePhotoAfter?.data || attrs.evidencePhotoAfter;
                          const imageUrl = getImageUrl(afterPhoto);
                          const hasImage = hasValidImage(afterPhoto) && !imageErrors.after && imageUrl;
                          
                          return hasImage ? (
                            <Box>
                              <img
                                src={imageUrl}
                                alt="After"
                                style={{ maxWidth: '300px', maxHeight: '300px', borderRadius: '4px', border: '1px solid #ddd' }}
                                onError={() => setImageErrors(prev => ({ ...prev, after: true }))}
                              />
                            </Box>
                          ) : (
                            <Box
                              sx={{
                                width: '300px',
                                height: '300px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '4px',
                                border: '1px solid #ddd',
                                backgroundColor: '#f5f5f5',
                                color: 'text.secondary'
                              }}
                            >
                              <Typography variant="body2">No image</Typography>
                            </Box>
                          );
                        })()}
                      </Box>
                    </Box>
                  </Box>
                </Stack>
              );
            })()}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRequestModalOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
  );
};

export default Reports;
